export const basePath = '/';

export const baseAppPath = '/app';
const applyBaseUrl = (url: string): string => `${baseAppPath}/${url}`;

export const organizationsPath: string = applyBaseUrl('organizations');
// Where "back to sign in" goes. Sign-in has no screen of its own — the gate renders it for any path
// without a session — so this is the address that says so, rather than an org route that only works
// because an unauthenticated visitor never arrives.
export const signInPath: string = applyBaseUrl('sign-in');
export const forgotPasswordPath: string = applyBaseUrl('forgot-password');
export const resetPasswordPath: string = applyBaseUrl('reset-password');
export const organizationsNewPath = `${organizationsPath}/new`;
export const organizationPath = (organizationId?: string): string => `${organizationsPath}/${organizationId}`;
export const sprintReportingPath = (organizationId: string): string =>
  `${organizationPath(organizationId)}/sprint-reporting`;
export const productsReportingPath = (organizationId: string): string =>
  `${organizationPath(organizationId)}/products-reporting`;
export const clientsReportingPath = (organizationId: string): string =>
  `${organizationPath(organizationId)}/client-reporting`;

/** Declared by ClientReportingRouter and matched on by ClientSidebar, so the two cannot drift apart. */
export const clientSprintRoute = 'products/:productId/sprints/:sprintId';

/** The product page under the client's portal: the same tabs we see, at the client's own addresses. */
export const clientProductPath = (organizationId: string, productId?: string): string =>
  `${clientsReportingPath(organizationId)}/products/${productId}`;
export const clientProductReportsPath = (organizationId: string, productId?: string): string =>
  `${clientProductPath(organizationId, productId)}/sprints`;

export const productReportingPath = (organizationId: string, productId: string, sprintId: string): string =>
  `${organizationPath(organizationId)}/products-reporting/products/${productId}/sprints/${sprintId}`;
export const clientReportingPath = (organizationId?: string, productId?: string, sprintId?: string): string =>
  `${organizationPath(organizationId)}/client-reporting/products/${productId}/sprints/${sprintId}`;

export const membersPath = (organizationId: string): string => `${organizationPath(organizationId)}/members`;
export const memberPath = (organizationId: string, membershipId: string): string =>
  `${membersPath(organizationId)}/${membershipId}`;

export const chaptersPath = (organizationId: string): string => `${organizationPath(organizationId)}/chapters`;

export const settingsPath = (organizationId: string): string => `${organizationPath(organizationId)}/settings`;
/** Opens Settings scrolled to the Phill bot section, where the sources are connected. */
export const PHILL_BOT_HASH = '#phill-bot';

export const clientsPath = (organizationId: string): string => `${organizationPath(organizationId)}/clients`;
export const clientPath = (organizationId: string, clientId: string): string =>
  `${clientsPath(organizationId)}/${clientId}`;
export type ClientPageTab = 'overview' | 'cost';
export const clientTabPath = (organizationId: string, clientId: string, tab: ClientPageTab): string =>
  tab === 'overview' ? clientPath(organizationId, clientId) : `${clientPath(organizationId, clientId)}/${tab}`;
/** The same page, opened at the people who can read that client's products. */
export const CLIENT_ACCESS_HASH = '#access';
export const clientAccessPath = (organizationId: string, clientId: string): string =>
  `${clientPath(organizationId, clientId)}${CLIENT_ACCESS_HASH}`;

export const membersReportingListPath = (organizationId: string): string =>
  `${organizationPath(organizationId)}/members-reporting`;
export const memberReportingPath = (orgId: string, memberId: string): string =>
  `${membersReportingListPath(orgId)}/${memberId}`;

/**
 * Insights is two tabs, each keeping its own address. Both are named below `/insights` rather than one of
 * them being `/insights` itself: the tab bar decides which tab is active by prefix, so a parent that is also
 * a tab would light up on both.
 */
export const incidentsPath = (organizationId: string): string => `${organizationPath(organizationId)}/incidents`;

export const incidentPath = (organizationId: string, productId: string, incidentId: string): string =>
  `${incidentsPath(organizationId)}/${productId}/${incidentId}`;
export const insightsPath = (organizationId: string): string => `${organizationPath(organizationId)}/insights`;
export const insightsOverviewPath = (organizationId: string): string => `${insightsPath(organizationId)}/overview`;
export const insightsExportsPath = (organizationId: string): string => `${insightsPath(organizationId)}/exports`;

export const productsPath = (organizationId: string): string => `${organizationPath(organizationId)}/products`;
export const productPath = (orgId: string, productId: string): string => `${productsPath(orgId)}/${productId}`;
// What the product is sold on: its team, their levels, the price list and the support agreement.
export const productTeamPath = (orgId: string, productId: string): string => `${productPath(orgId, productId)}/team`;
export const productBudgetPath = (orgId: string, productId: string): string =>
  `${productPath(orgId, productId)}/budget`;
export type ProductPageTab =
  | 'dashboard'
  | 'settings'
  | 'team'
  | 'sprints'
  | 'budget'
  | 'costs'
  | 'incidents'
  | 'quality'
  | 'productivity'
  | 'security-audit'
  | 'monitoring';

/** One product page for everyone: under products for us, under the client's portal for them. */
export const productTabPath = (orgId: string, productId: string, tab: ProductPageTab, asClient = false): string => {
  const base = asClient ? clientProductPath(orgId, productId) : productPath(orgId, productId);
  return tab === 'dashboard' ? base : `${base}/${tab}`;
};

export const productIncidentsPath = (orgId: string, productId: string): string =>
  `${productPath(orgId, productId)}/incidents`;

export const clientReportingSlugPath = clientReportingPath(':organizationId', ':productId', ':sprintId');

/** For `matchPath(..., { end: false })` — any deep link under org client-reporting (not only full slug). */
export const clientReportingRoutePrefixPattern = `${organizationsPath}/:organizationId/client-reporting`;
